import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { 
  ArrowRight,
  Landmark,
  Building2,
  TrendingUp,
  Wallet,
  Truck,
  Factory,
  Layers,
  Award,
  CheckCircle,
  Clock,
  Users,
  Briefcase
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { useLanguage } from '../contexts/LanguageContext';

const CreditoOperacoes = () => {
  const { t } = useLanguage();

  const creditTypes = [
    { icon: <Wallet className="h-6 w-6" />, titleKey: 'creditPage.type1', descKey: 'creditPage.type1Desc' },
    { icon: <TrendingUp className="h-6 w-6" />, titleKey: 'creditPage.type2', descKey: 'creditPage.type2Desc' },
    { icon: <Truck className="h-6 w-6" />, titleKey: 'creditPage.type3', descKey: 'creditPage.type3Desc' },
    { icon: <Factory className="h-6 w-6" />, titleKey: 'creditPage.type4', descKey: 'creditPage.type4Desc' },
    { icon: <Layers className="h-6 w-6" />, titleKey: 'creditPage.type5', descKey: 'creditPage.type5Desc' },
    { icon: <Award className="h-6 w-6" />, titleKey: 'creditPage.type6', descKey: 'creditPage.type6Desc' },
  ];

  const processSteps = [
    { step: 1, titleKey: 'creditPage.process1', descKey: 'creditPage.process1Desc' },
    { step: 2, titleKey: 'creditPage.process2', descKey: 'creditPage.process2Desc' },
    { step: 3, titleKey: 'creditPage.process3', descKey: 'creditPage.process3Desc' },
    { step: 4, titleKey: 'creditPage.process4', descKey: 'creditPage.process4Desc' },
  ];

  const whyChoose = [
    'creditPage.whyChoose1',
    'creditPage.whyChoose2',
    'creditPage.whyChoose3',
    'creditPage.whyChoose4',
    'creditPage.whyChoose5',
    'creditPage.whyChoose6',
  ];

  const networkItems = [
    { icon: <Building2 className="h-6 w-6" />, titleKey: 'creditPage.banks', descKey: 'creditPage.banksDesc' },
    { icon: <Briefcase className="h-6 w-6" />, titleKey: 'creditPage.funds', descKey: 'creditPage.fundsDesc' },
    { icon: <Users className="h-6 w-6" />, titleKey: 'creditPage.creditors', descKey: 'creditPage.creditorsDesc' },
    { icon: <TrendingUp className="h-6 w-6" />, titleKey: 'creditPage.investors', descKey: 'creditPage.investorsDesc' },
  ];

  return (
    <div className="min-h-screen pt-20" data-testid="credito-operacoes-page">
      {/* Hero Section */}
      <section className="py-24 md:py-32 bg-stone-50 noise-bg">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
            >
              <span className="text-bronze font-medium text-sm tracking-wider uppercase mb-4 block">
                {t('creditPage.heroTag')}
              </span>
              <h1 className="text-4xl md:text-5xl font-serif font-bold text-stone-900 mb-6">
                {t('creditPage.heroTitle')}{' '}
                <span className="gold-sheen">{t('creditPage.heroHighlight')}</span>
              </h1>
              <p className="text-lg text-stone-600 mb-8 leading-relaxed">
                {t('creditPage.heroDesc')}
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Button 
                  asChild
                  className="bg-emerald hover:bg-emerald-light text-white rounded-sm px-8 py-6 text-lg font-serif btn-shine"
                  data-testid="credit-cta-primary"
                >
                  <Link to="/contato" className="flex items-center gap-2">
                    {t('creditPage.ctaPrimary')}
                    <ArrowRight className="h-5 w-5" />
                  </Link>
                </Button>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              className="hidden lg:block"
            >
              <div className="relative">
                <div className="absolute -inset-4 bg-emerald/10 rounded-sm blur-2xl" />
                <img 
                  src="https://images.unsplash.com/photo-1559526324-4b87b5e36e44?w=800&q=80"
                  alt="Financial operations"
                  className="relative rounded-sm shadow-2xl w-full"
                />
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="py-16 bg-emerald">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {[
              { number: '200+', labelKey: 'creditPage.statsPartners' },
              { number: 'R$ 500mi+', labelKey: 'creditPage.statsOperations' },
              { number: '100+', labelKey: 'creditPage.statsAgents' },
              { number: '48h', labelKey: 'creditPage.statsResponse' },
            ].map((stat, index) => (
              <motion.div
                key={stat.labelKey}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="text-center"
              >
                <p className="text-3xl md:text-4xl font-serif font-bold text-white mb-2">
                  {stat.number}
                </p>
                <p className="text-stone-300 text-sm">{t(stat.labelKey)}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Credit Types */}
      <section className="py-24 md:py-32 bg-white" data-testid="credit-types">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-16">
            <span className="text-bronze font-medium text-sm tracking-wider uppercase mb-4 block">
              {t('creditPage.typesTag')}
            </span>
            <h2 className="text-3xl md:text-4xl font-serif font-bold text-stone-900 mb-4">
              {t('creditPage.typesTitle')}
            </h2>
            <p className="text-stone-600 max-w-2xl mx-auto">
              {t('creditPage.typesDesc')}
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {creditTypes.map((credit, index) => (
              <motion.div
                key={credit.titleKey}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="bg-stone-50 p-6 rounded-sm border border-stone-200 hover:shadow-md hover:-translate-y-1 transition-all duration-300"
                data-testid={`credit-type-${index}`}
              >
                <div className="w-12 h-12 rounded-sm bg-emerald/10 flex items-center justify-center text-emerald mb-4">
                  {credit.icon}
                </div>
                <h3 className="text-lg font-serif font-semibold text-stone-900 mb-2">
                  {t(credit.titleKey)}
                </h3>
                <p className="text-stone-600 text-sm">
                  {t(credit.descKey)}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Process */}
      <section className="py-24 md:py-32 bg-stone-50 noise-bg" data-testid="credit-process">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-16">
            <span className="text-bronze font-medium text-sm tracking-wider uppercase mb-4 block">
              {t('creditPage.processTag')}
            </span>
            <h2 className="text-3xl md:text-4xl font-serif font-bold text-stone-900">
              {t('creditPage.processTitle')}
            </h2>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {processSteps.map((step, index) => (
              <motion.div
                key={step.titleKey}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="relative"
                data-testid={`credit-step-${index}`}
              >
                {index < processSteps.length - 1 && (
                  <div className="hidden lg:block absolute top-8 left-1/2 w-full h-0.5 bg-stone-200" />
                )}
                <div className="relative text-center">
                  <div className="w-16 h-16 rounded-full bg-emerald text-white flex items-center justify-center mx-auto mb-4 relative z-10 text-xl font-serif font-bold">
                    {step.step}
                  </div>
                  <h3 className="text-lg font-serif font-semibold text-stone-900 mb-2">
                    {t(step.titleKey)}
                  </h3>
                  <p className="text-stone-600 text-sm">
                    {t(step.descKey)}
                  </p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-24 md:py-32 bg-emerald text-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <h2 className="text-3xl md:text-4xl font-serif font-bold mb-8">
                {t('creditPage.whyChooseTitle')}
              </h2>
              <div className="grid grid-cols-1 gap-4">
                {whyChoose.map((itemKey, index) => (
                  <div 
                    key={itemKey}
                    className="flex items-center gap-4 bg-white/10 p-4 rounded-sm"
                  >
                    <CheckCircle className="h-5 w-5 text-bronze flex-shrink-0" />
                    <span className="text-stone-200">{t(itemKey)}</span>
                  </div>
                ))}
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <div className="text-center mb-8">
                <span className="text-bronze font-medium text-sm tracking-wider uppercase mb-4 block">
                  {t('creditPage.networkTag')}
                </span>
                <h3 className="text-2xl font-serif font-bold">
                  {t('creditPage.networkTitle')}
                </h3>
              </div>
              <div className="grid grid-cols-2 gap-4">
                {networkItems.map((item) => (
                  <div 
                    key={item.titleKey}
                    className="bg-white/10 backdrop-blur-sm p-6 rounded-sm text-center"
                  >
                    <div className="w-12 h-12 rounded-sm bg-bronze/20 flex items-center justify-center text-bronze mx-auto mb-3">
                      {item.icon}
                    </div>
                    <h4 className="font-semibold mb-1">{t(item.titleKey)}</h4>
                    <p className="text-stone-300 text-sm">{t(item.descKey)}</p>
                  </div>
                ))}
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 md:py-32 bg-stone-50 noise-bg">
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <Landmark className="h-16 w-16 text-bronze mx-auto mb-6" />
            <h2 className="text-3xl md:text-4xl font-serif font-bold text-stone-900 mb-6">
              {t('creditPage.ctaTitle')}
            </h2>
            <p className="text-stone-600 text-lg mb-8">
              {t('creditPage.ctaDesc')}
            </p>
            <Button 
              asChild
              className="bg-emerald hover:bg-emerald-light text-white rounded-sm px-8 py-6 text-lg font-serif btn-shine"
              data-testid="credit-final-cta"
            >
              <Link to="/contato" className="flex items-center gap-2">
                {t('creditPage.ctaBtn')}
                <ArrowRight className="h-5 w-5" />
              </Link>
            </Button>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default CreditoOperacoes;
